// src/App.jsx
import React, { Suspense, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";

import Header from "./components/Header.jsx";
import ConsentBanner from "./components/ConsentBanner.jsx";
import MissingKeyBadge from "./components/MissingKeyBadge.jsx";
import MaintenanceGate from "./components/MaintenanceGate.jsx";

/** Global keyboard guard */
function KeyboardSafety() {
    useEffect(() => {
        const handler = (e) => {
            const el = e.target;
            const tag = el?.tagName;
            const isFormEl =
                tag === "INPUT" ||
                tag === "TEXTAREA" ||
                tag === "SELECT" ||
                el?.isContentEditable;

            // Prevent Backspace from navigating when not in a field
            if (e.key === "Backspace" && !isFormEl) {
                e.preventDefault();
                e.stopPropagation();
            }

            // Prevent stray Enter from triggering buttons/links outside forms
            if (e.key === "Enter" && !isFormEl) {
                e.stopPropagation();
            }
        };

        document.addEventListener("keydown", handler, true);
        return () => document.removeEventListener("keydown", handler, true);
    }, []);

    return null;
}

// Keep token checker disabled while auth stabilizes
function ScopedTokenCheckerDisabled() {
    return null;
}

/** ✅ Truth detector (DEV ONLY) */
function RouterProbe() {
    const loc = useLocation();

    useEffect(() => {
        console.log("[RouterProbe] location changed =>", loc.pathname, loc.search);
    }, [loc.pathname, loc.search]);

    if (!import.meta.env.DEV) return null;

    return (
        <div
            style={{
                position: "sticky",
                top: 0,
                zIndex: 9999,
                background: "#0f172a",
                color: "white",
                fontSize: 12,
                padding: "6px 10px",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
            }}
        >
            ROUTER PATH: <strong>{loc.pathname}</strong>
            {loc.search ? <span> | {loc.search}</span> : null}
        </div>
    );
}

export default function App() {
    const location = useLocation();
    const path = location.pathname || "";

    const isDashboard = path.startsWith("/dashboard");
    const isAdmin = path.startsWith("/admin");

    // ✅ Treat public profile pages as wide too (prevents layout squish)
    // Add/adjust prefixes here if your route is different.
    const isPublicBusinessProfile =
        path.startsWith("/business/") ||
        path.startsWith("/public/") ||
        path.startsWith("/profile/") ||
        path.startsWith("/listing/");

    const wideContent = isDashboard || isAdmin || isPublicBusinessProfile;

    // ✅ turn this on only when you need the bar
    const SHOW_ROUTER_PROBE = false;

    return (
        <MaintenanceGate>
            <div className="min-h-screen flex flex-col app-shell">
                <KeyboardSafety />
                <ScopedTokenCheckerDisabled />

                {SHOW_ROUTER_PROBE ? <RouterProbe /> : null}

                <Header />

                <main className="flex-1 w-full">
                    <div className="app-shell-inner">
                        <div className={wideContent ? "app-page-wide" : "app-page"}>
                            <Suspense
                                fallback={
                                    <div className="text-center py-8" aria-live="polite">
                                        🌐 Loading…
                                    </div>
                                }
                            >
                                <Outlet />
                            </Suspense>
                        </div>
                    </div>
                </main>

                <ConsentBanner />
                <MissingKeyBadge />
            </div>
        </MaintenanceGate>
    );
}
